/**
 * OnboardingPage — Full-page component shown when user has no teams.
 * Presents two action paths: "Create a team" and "Join an existing team".
 * No skip/dismiss option — a team is required to proceed.
 * @module components/starnexus/team/OnboardingPage
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8
 */

import React, { useState } from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Alert from '@amzn/awsui-components-console/alert';
import Textarea from '@amzn/awsui-components-console/textarea';
import Box from '@amzn/awsui-components-console/box';
import { useAuthStore } from '../../../stores/starnexus/authStore';
import { apiUrl, apiFetch } from '../../../utils/apiClient';
import { Team } from '../../../types/starnexus/team';

// ============================================================================
// Constants
// ============================================================================

/** teamId slug pattern: lowercase alphanumeric with hyphens, 3-50 chars */
const TEAM_ID_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
const TEAM_ID_MIN_LENGTH = 3;
const TEAM_ID_MAX_LENGTH = 50;
const TEAM_NAME_MAX_LENGTH = 100;

// ============================================================================
// Types
// ============================================================================

type OnboardingMode = 'choose' | 'create' | 'join';

// ============================================================================
// Validation Helpers
// ============================================================================

function validateTeamId(value: string): string | null {
  if (!value.trim()) {
    return 'Team ID is required';
  }
  if (value.length < TEAM_ID_MIN_LENGTH) {
    return `Team ID must be at least ${TEAM_ID_MIN_LENGTH} characters`;
  }
  if (value.length > TEAM_ID_MAX_LENGTH) {
    return `Team ID must be at most ${TEAM_ID_MAX_LENGTH} characters`;
  }
  if (!TEAM_ID_PATTERN.test(value)) {
    return 'Team ID must contain only lowercase letters, numbers, and hyphens. Must start and end with a letter or number.';
  }
  return null;
}

function validateTeamName(value: string): string | null {
  if (!value.trim()) {
    return 'Team name is required';
  }
  if (value.length > TEAM_NAME_MAX_LENGTH) {
    return `Team name must be at most ${TEAM_NAME_MAX_LENGTH} characters`;
  }
  return null;
}

function validateJoinSlug(value: string): string | null {
  if (!value.trim()) {
    return 'Team slug is required';
  }
  return null;
}

// ============================================================================
// OnboardingPage Component
// ============================================================================

export const OnboardingPage: React.FC = () => {
  const addTeam = useAuthStore((state) => state.addTeam);

  // Mode state
  const [mode, setMode] = useState<OnboardingMode>('choose');

  // Create flow state
  const [teamId, setTeamId] = useState('');
  const [teamName, setTeamName] = useState('');
  const [description, setDescription] = useState('');
  const [createErrors, setCreateErrors] = useState<{
    teamId?: string;
    teamName?: string;
  }>({});
  const [createApiError, setCreateApiError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);

  // Join flow state
  const [joinSlug, setJoinSlug] = useState('');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinApiError, setJoinApiError] = useState<string | null>(null);
  const [joinLoading, setJoinLoading] = useState(false);

  // ============================================================================
  // Create Flow Handlers
  // ============================================================================

  const handleCreate = async () => {
    // Client-side validation
    const teamIdError = validateTeamId(teamId);
    const teamNameError = validateTeamName(teamName);

    if (teamIdError || teamNameError) {
      setCreateErrors({
        teamId: teamIdError ?? undefined,
        teamName: teamNameError ?? undefined,
      });
      return;
    }

    setCreateErrors({});
    setCreateApiError(null);
    setCreateLoading(true);

    try {
      const res = await apiFetch(apiUrl('/team/teams'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamId,
          teamName,
          description: description || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ message: 'Failed to create team' }));
        if (res.status === 409) {
          setCreateApiError(data.message ?? `Team '${teamId}' already exists`);
        } else {
          setCreateApiError(data.message ?? `Failed to create team (${res.status})`);
        }
        return;
      }

      const team: Team = await res.json();
      addTeam(team);
    } catch (err) {
      setCreateApiError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ============================================================================
  // Join Flow Handlers
  // ============================================================================

  const handleJoin = async () => {
    // Client-side validation
    const slugError = validateJoinSlug(joinSlug);
    if (slugError) {
      setJoinError(slugError);
      return;
    }

    setJoinError(null);
    setJoinApiError(null);
    setJoinLoading(true);

    try {
      const res = await apiFetch(apiUrl(`/team/teams/${encodeURIComponent(joinSlug.trim())}/join`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ message: 'Failed to join team' }));
        if (res.status === 404) {
          setJoinApiError(data.message ?? 'Team not found');
        } else if (res.status === 409) {
          setJoinApiError(data.message ?? 'Already a member of this team');
        } else {
          setJoinApiError(data.message ?? `Failed to join team (${res.status})`);
        }
        return;
      }

      const team: Team = await res.json();
      addTeam(team);
    } catch (err) {
      setJoinApiError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setJoinLoading(false);
    }
  };

  // ============================================================================
  // Render: Choose Mode
  // ============================================================================

  if (mode === 'choose') {
    return (
      <Box padding={{ vertical: 'xxxl', horizontal: 'l' }}>
        <SpaceBetween size="l" direction="vertical" alignItems="center">
          <Box textAlign="center">
            <SpaceBetween size="xs">
              <Box variant="h1" fontSize="heading-xl">
                Welcome to StarNexus
              </Box>
              <Box variant="p" color="text-body-secondary">
                To get started, create a new team or join an existing one.
              </Box>
            </SpaceBetween>
          </Box>

          <SpaceBetween size="l" direction="horizontal">
            <Container
              header={<Header variant="h2">Create a team</Header>}
            >
              <SpaceBetween size="m">
                <Box variant="p">
                  Start fresh with a new team. You will be the owner and can invite others later.
                </Box>
                <Button variant="primary" onClick={() => setMode('create')}>
                  Create a team
                </Button>
              </SpaceBetween>
            </Container>

            <Container
              header={<Header variant="h2">Join an existing team</Header>}
            >
              <SpaceBetween size="m">
                <Box variant="p">
                  Join a team that already exists by entering its team slug.
                </Box>
                <Button variant="primary" onClick={() => setMode('join')}>
                  Join a team
                </Button>
              </SpaceBetween>
            </Container>
          </SpaceBetween>
        </SpaceBetween>
      </Box>
    );
  }

  // ============================================================================
  // Render: Create Flow
  // ============================================================================

  if (mode === 'create') {
    return (
      <Box padding={{ vertical: 'xxxl', horizontal: 'l' }}>
        <SpaceBetween size="l" direction="vertical" alignItems="center">
          <Container
            header={<Header variant="h2">Create a team</Header>}
          >
            <SpaceBetween size="m">
              {createApiError && (
                <Alert type="error">{createApiError}</Alert>
              )}

              <FormField
                label="Team ID"
                description="A unique slug for your team (lowercase letters, numbers, and hyphens). 3-50 characters."
                errorText={createErrors.teamId}
                constraintText="Must start and end with a letter or number. Example: my-team-01"
              >
                <Input
                  value={teamId}
                  onChange={({ detail }) => {
                    setTeamId(detail.value);
                    if (createErrors.teamId) {
                      setCreateErrors((prev) => ({ ...prev, teamId: undefined }));
                    }
                  }}
                  placeholder="my-team-name"
                />
              </FormField>

              <FormField
                label="Team name"
                description="A display name for your team. 1-100 characters."
                errorText={createErrors.teamName}
              >
                <Input
                  value={teamName}
                  onChange={({ detail }) => {
                    setTeamName(detail.value);
                    if (createErrors.teamName) {
                      setCreateErrors((prev) => ({ ...prev, teamName: undefined }));
                    }
                  }}
                  placeholder="My Team Name"
                />
              </FormField>

              <FormField
                label={<span>Description <i>- optional</i></span>}
                description="A brief description of your team."
              >
                <Textarea
                  value={description}
                  onChange={({ detail }) => setDescription(detail.value)}
                  placeholder="What does this team work on?"
                />
              </FormField>

              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={() => setMode('choose')}>Back</Button>
                <Button
                  variant="primary"
                  onClick={handleCreate}
                  loading={createLoading}
                >
                  Create team
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Box>
    );
  }

  // ============================================================================
  // Render: Join Flow
  // ============================================================================

  return (
    <Box padding={{ vertical: 'xxxl', horizontal: 'l' }}>
      <SpaceBetween size="l" direction="vertical" alignItems="center">
        <Container
          header={<Header variant="h2">Join an existing team</Header>}
        >
          <SpaceBetween size="m">
            {joinApiError && (
              <Alert type="error">{joinApiError}</Alert>
            )}

            <FormField
              label="Team slug"
              description="Enter the team slug (ID) of the team you want to join."
              errorText={joinError}
            >
              <Input
                value={joinSlug}
                onChange={({ detail }) => {
                  setJoinSlug(detail.value);
                  if (joinError) {
                    setJoinError(null);
                  }
                }}
                placeholder="team-slug"
              />
            </FormField>

            <SpaceBetween size="xs" direction="horizontal">
              <Button onClick={() => setMode('choose')}>Back</Button>
              <Button
                variant="primary"
                onClick={handleJoin}
                loading={joinLoading}
              >
                Join team
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Box>
  );
};

export default OnboardingPage;
